import { SETTLEMENTS_ENDPOINT, TENANT_SETTINGS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { Settlement } from "../../../../types/settlements";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Chip, Typography, Divider } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatNumber } from "../../../../utils";
import {
  getSettlementContractId,
  getSettlementContractLabel,
  getSettlementDisplayLabel,
  getSettlementTermEquipments,
} from "../../../../utils/settlementContractTerms";
import { TenantSettings } from "../../../../types/tenantSettings";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { useHasPermission } from "../../../../contexts/auth";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "240px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

export const SettlementBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(SETTLEMENTS_ENDPOINT);
  const { get: getTenantSettings } = useCRUD(TENANT_SETTINGS_ENDPOINT);
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const canViewContract = useHasPermission("settlement.view_contract");

  useEffect(() => {
    const fetchSettlement = async () => {
      if (id) {
        const response = await get(id);
        setSettlement(response.data);
      }
    };

    const fetchTenantSettings = async () => {
      const response = await getTenantSettings("settings");
      if (response.data) {
        setTenantSettings(response.data);
      }
    };

    const handleSettlementUpdated = () => {
      fetchSettlement();
    };

    window.addEventListener("settlementUpdated", handleSettlementUpdated);
    fetchSettlement();
    fetchTenantSettings();
    return () => {
      window.removeEventListener("settlementUpdated", handleSettlementUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateDaysUntilReminder = (): number | null => {
    if (!settlement?.last_reminder_at || !tenantSettings?.next_reminder_available_after_days) {
      return null;
    }

    const lastReminderDate = new Date(settlement.last_reminder_at);
    const today = new Date();
    const daysSinceLastReminder = Math.floor(
      (today.getTime() - lastReminderDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysLeft = tenantSettings.next_reminder_available_after_days - daysSinceLastReminder;

    return daysLeft > 0 ? daysLeft : 0;
  };

  if (!settlement) return <CustomCircularProgress />;

  const contractTerm = settlement.contract_term;
  const contractTermEquipments = contractTerm
    ? getSettlementTermEquipments(contractTerm)
    : [];
  const isBrutto = Boolean(contractTerm?.contract_info?.is_brutto);

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {t("Settlement")} - {getSettlementDisplayLabel(settlement, t, id ?? "-")}
          </Typography>
          {isBrutto && (
            <Chip
              label={t("Gross (Brutto)")}
              color="warning"
              size="small"
              title={t("This contract's values are stored and settled as gross (brutto) amounts")}
            />
          )}
        </Box>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DetailRow label={t("Status")}>
            <Typography variant="subtitle1">{settlement.status_display}</Typography>
          </DetailRow>
          <DetailRow label={t("Created at")}>
            <Typography variant="subtitle1">{formatDateTime(settlement.created_at)}</Typography>
          </DetailRow>
          {settlement.sale_date && (
            <DetailRow label={t("Sale date")}>
              <Typography variant="subtitle1">{settlement.sale_date}</Typography>
            </DetailRow>
          )}
          {settlement.show_inflation_rate_percent && (
            <DetailRow label={t("Inflation rate (%)")}>
              <Typography variant="subtitle1">
                {settlement.inflation_rate_percent != null && settlement.inflation_rate_percent !== ""
                  ? formatNumber(settlement.inflation_rate_percent)
                  : "-"}
              </Typography>
            </DetailRow>
          )}
          <DetailRow label={t("Total Calculated")}>
            <Typography variant="subtitle1">{formatNumber(settlement.total_calculated)}</Typography>
          </DetailRow>
          {settlement.total_overridden && (
            <DetailRow label={t("Total Overridden")}>
              <Typography variant="subtitle1">{formatNumber(settlement.total_overridden)}</Typography>
            </DetailRow>
          )}
          {settlement.confirmed_date && (
            <DetailRow label={t("Confirmed date")}>
              <Typography variant="subtitle1">{formatDateTime(settlement.confirmed_date)}</Typography>
            </DetailRow>
          )}
          {settlement.confirmed_by && (
            <DetailRow label={t("Confirmed By")}>
              <Typography variant="subtitle1">{settlement.confirmed_by.email || "-"}</Typography>
            </DetailRow>
          )}
          {settlement.last_reminder_at && (
            <DetailRow label={t("Last reminder at")}>
              <Typography variant="subtitle1">{formatDateTime(settlement.last_reminder_at)}</Typography>
            </DetailRow>
          )}
          {(() => {
            const daysLeft = calculateDaysUntilReminder();
            if (daysLeft !== null) {
              return (
                <DetailRow label={t("Next reminder can be sent after")} noBorder>
                  <Typography variant="subtitle1">
                    {daysLeft} {daysLeft === 1 ? t("day") : t("days")}
                  </Typography>
                </DetailRow>
              );
            }
            return null;
          })()}
        </Box>
      </Box>

      <Divider />

      {/* Contract Term Information Section */}
      {contractTerm && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {t("Equipment Information")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {contractTermEquipments.map((equipment, index) => (
              <Typography key={equipment.id ?? index} variant="subtitle1">
                <strong>{t("Equipment")}:</strong>{" "}
                <EntityReferenceLink
                  entityType="equipment"
                  entityId={equipment.id}
                  label={equipment.full_name || "-"}
                  canPreview={canViewEquipment}
                  stopPropagation={false}
                />
              </Typography>
            ))}
            <Typography variant="subtitle1">
              <strong>{t("Settlement Cycle")}:</strong>{" "}
              {contractTerm.settlement_cycle_display || "-"}
            </Typography>
            <Typography variant="subtitle1">
              <strong>{isBrutto ? t("Gross rent price") : t("Net rent price")}:</strong>{" "}
              {formatNumber(contractTerm.rent_price)}
            </Typography>
            {contractTerm.contract_info && (
              <>
                <Typography variant="subtitle1">
                  <strong>{t("Contract Number")}:</strong>{" "}
                  <EntityReferenceLink
                    entityType="contract"
                    entityId={getSettlementContractId(settlement)}
                    label={getSettlementContractLabel(settlement)}
                    canPreview={canViewContract}
                    stopPropagation={false}
                  />
                </Typography>
                <Typography variant="subtitle1">
                  <strong>{t("Contract Start Date")}:</strong>{" "}
                  {contractTerm.contract_info.start_date}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>{t("Contract End Date")}:</strong>{" "}
                  {contractTerm.contract_info.end_date}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
